import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, CheckCircle2, Zap, Play, Sparkles, Wand2, Bot, FileText, MessageSquareCode, Brain, GanttChart, MessageSquare, ThumbsUp, Users } from 'lucide-react';
import Logo from '../components/Logo';
import { supabase } from '../lib/supabase';
import Chatbot from '../components/Chatbot';

interface HomeProps {
  navigate: (to: string) => void;
  faqOnly?: boolean;
}

function Home({ navigate, faqOnly = false }: HomeProps) {
  const [showFAQ, setShowFAQ] = useState(false);
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);

  // If faqOnly is true, automatically show the FAQ section
  useEffect(() => {
    if (faqOnly) {
      setShowFAQ(true);
      // Scroll to FAQ section
      setTimeout(() => {
        const faqElement = document.getElementById('faq-section');
        if (faqElement) {
          faqElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [faqOnly]);

  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=2000&q=80',
      title: 'Streamline Your Workflow',
      description: 'Manage projects and tasks all in one place with our comprehensive solution.'
    },
    {
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=2000&q=80',
      title: 'AI-Powered Insights',
      description: 'Make informed decisions with real-time analytics and AI-powered recommendations.'
    },
    {
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=2000&q=80',
      title: 'Data-Driven Insights',
      description: 'Make informed decisions with real-time analytics and project metrics.'
    }
  ];

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=2000&q=80"
            alt="Hero background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        </div>

        {/* Logo and Hero Content */}
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 lg:px-8">
          <Logo size="lg" className="mb-12" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 max-w-4xl">
            Welcome to PM Pro AI
          </h1>
          <p className="mt-4 text-xl text-white/90 max-w-2xl mx-auto mb-8">
            Your personalized project management tool powered by artificial intelligence
          </p>
          <button 
            onClick={() => navigate('/auth')} 
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          > 
            Get Started Free
            <ArrowRight className="ml-2 h-6 w-6" />
          </button>
        </div>
      </div>

      {/* About Section */}
      <div className="relative py-24 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:gap-16">
            {/* Image Side */}
            <div className="lg:w-1/2 mb-12 lg:mb-0">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-3xl blur-2xl" />
                <img
                  src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=2000&q=80"
                  alt="Project management"
                  className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10 dark:ring-white/10" />
              </div>
            </div>

            {/* Content Side */}
            <div className="lg:w-1/2">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl mb-6">
                Transform Your Project Management with AI
              </h2>
              <div className="space-y-6 text-lg text-gray-600 dark:text-gray-300">
                <p>
                  PM Pro AI is a revolutionary project management platform that combines traditional project management with advanced AI capabilities, including a comprehensive prompt library and custom prompt generation for optimized workflows.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                      <MessageSquareCode className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">AI-Powered Insights</h3>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        Leverage AI to generate task descriptions, research insights, and work assignments.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <MessageSquareCode className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">Prompt Library</h3>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        Access our extensive library of AI prompts or create custom prompts for specialized project needs.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">AI Agents</h3>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        Specialized AI agents for task automation, meeting summaries, and project research.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-6">
                  <button
                    onClick={() => navigate('/auth')}
                    className="inline-flex items-center px-6 py-3 text-base font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg"
                  >
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="py-16 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-gray-900 dark:to-indigo-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-indigo-600 dark:text-indigo-400 font-semibold tracking-wide uppercase flex items-center justify-center">
              <Sparkles className="h-5 w-5 mr-2" />
              Powerful Features
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Manage projects effectively
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 dark:text-gray-300 lg:mx-auto">
              Everything you need to streamline your workflow and boost productivity
            </p>
          </div>

          <div className="mt-10 relative">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')] opacity-5 bg-fixed bg-cover bg-center rounded-xl"></div>
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
                    <FileText className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                    Notes & Documentation
                  </p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500 dark:text-gray-300">
                  Smart note-taking with reminders, rich text editing, and seamless organization. Set reminders for important notes and never miss critical information.
                </dd>
              </div>

              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                    Task Management
                  </p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500 dark:text-gray-300">
                    Intuitive task creation, smart assignments, and real-time progress tracking with AI-powered insights and comprehensive reporting.
                </dd>
              </div>
              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
                    <Zap className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                    Powerful Analytics
                  </p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500 dark:text-gray-300">
                    Advanced analytics, customizable dashboards, AI-driven insights, and detailed project reports with key metrics and insights.
                </dd>
              </div>
              <div className="relative">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
                    <FileText className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                    Comprehensive Reports
                  </p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500 dark:text-gray-300">
                    Generate detailed weekly and monthly reports with project snapshots, team metrics, and AI-powered recommendations. Export in multiple formats.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* AI Agents Section */}
      <div className="bg-gradient-to-b from-white to-indigo-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Supercharge Your Workflow with AI Agents
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Our specialized AI agents are designed to enhance your project management experience, automating complex tasks and providing intelligent insights.
            </p>
          </div>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-8">
            {/* Agent 1 */}
            <div className="flex flex-col rounded-3xl bg-white shadow-xl ring-1 ring-gray-200 hover:ring-indigo-500 hover:shadow-2xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                    <Brain className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Project Methodology Advisor</h3>
                </div>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  Get expert recommendations on the best project management methodology for your specific project needs.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Analyzes project requirements</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Evaluates team structure</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Considers timeline constraints</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Agent 2 */}
            <div className="flex flex-col rounded-3xl bg-white shadow-xl ring-1 ring-gray-200 hover:ring-indigo-500 hover:shadow-2xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                    <GanttChart className="h-6 w-6 text-yellow-500" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Gantt Chart Generator</h3>
                </div>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  Create visual project timelines and dependencies with our intelligent Gantt chart generator.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Task breakdown and sequencing</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Timeline visualization</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Dependency mapping</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Agent 3 */}
            <div className="flex flex-col rounded-3xl bg-white shadow-xl ring-1 ring-gray-200 hover:ring-indigo-500 hover:shadow-2xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                    <ThumbsUp className="h-6 w-6 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Customer Mood Analyzer</h3>
                </div>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  Analyze client feedback to determine mood and provide actionable recommendations.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Detect client mood and sentiment</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Provide sentiment score</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Suggest actionable responses</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Agent 4 */}
            <div className="flex flex-col rounded-3xl bg-white shadow-xl ring-1 ring-gray-200 hover:ring-indigo-500 hover:shadow-2xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                    <MessageSquare className="h-6 w-6 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Meeting Summarizer</h3>
                </div>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  Convert meeting notes into structured summaries with action items and key decisions.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Extract key meeting details</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Identify action items and owners</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Track meeting attendance</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Agent 5 */}
            <div className="flex flex-col rounded-3xl bg-white shadow-xl ring-1 ring-gray-200 hover:ring-indigo-500 hover:shadow-2xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                    <Zap className="h-6 w-6 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Productivity Improver</h3>
                </div>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  Get a personalized productivity plan to overcome challenges and achieve your goals.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Analyze productivity challenges</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Create personalized daily schedule</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Provide actionable improvement steps</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Agent 6 */}
            <div className="flex flex-col rounded-3xl bg-white shadow-xl ring-1 ring-gray-200 hover:ring-indigo-500 hover:shadow-2xl transition-all duration-300">
              <div className="p-8">
                <div className="flex items-center gap-x-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
                    <Bot className="h-6 w-6 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Custom AI Agents</h3>
                </div>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  Create your own specialized AI agents tailored to your specific business needs.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-600">
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Define custom agent goals</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Create specialized instructions</span>
                  </li>
                  <li className="flex gap-x-3">
                    <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600" />
                    <span>Attach reference documents</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative bg-gradient-to-br from-indigo-900 to-purple-900 dark:from-gray-900 dark:to-indigo-900">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=2000&q=80"
            alt="Background pattern"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Transform Your Project Management
          </h2>
          <p className="mt-4 text-xl text-indigo-200">
            Start managing your projects today.
          </p>
          <div className="mt-8">
            <button
              onClick={() => navigate('/auth')}
              className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
              Everything you need to know about PM Pro AI
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "What makes PM Pro AI different from other project management systems?",
                answer: "PM Pro AI combines classic project management features with smart AI tools that help automate planning, reporting, and team coordination for better productivity."
              },
              {
                question: "How does the AI research feature help with project planning?",
                answer: "You can research different projects and tasks using AI, get summarized insights quickly, and download the results for future reference or sharing."
              },
              {
                question: "What are AI agents in this app?",
                answer: "There are pre-built agents to automate repetitive tasks, and you can also create your own custom agents by adding specific instructions and documents to generate relevant outputs."
              },
              {
                question: "How do I get started with AI agents?",
                answer: "Just add your OpenAI API key in the settings, and you'll be able to start using or building AI agents instantly."
              },
              {
                question: "Can I generate reports for my projects and customers?",
                answer: "Yes, PM Pro AI allows you to generate AI-powered weekly or monthly reports specifically for your clients. You can also create custom report formats to deliver outputs tailored to each client's needs."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                <details className="group">
                  <summary 
                    className="flex justify-between items-center cursor-pointer px-6 py-4 focus:outline-none"
                    onClick={(e) => {
                      // Prevent default behavior to handle toggle manually
                      e.preventDefault();
                      // Find the closest details element
                      const details = e.currentTarget.closest('details');
                      if (details) {
                        // Toggle the open state
                        details.open = !details.open;
                      }
                    }}
                  >
                    <span className="text-lg font-medium text-gray-900 dark:text-white">{faq.question}</span>
                    <svg 
                      className="h-5 w-5 text-gray-500 dark:text-gray-400 transform transition-transform duration-300 group-open:rotate-180" 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 dark:text-gray-300">{faq.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;